import { FormEvent, useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabase";
import { CurrencyInput } from "./CurrencyInput";
import "./sale-financial.css";
import "./sale-payment-dialog.css";
import "./l1-financial.css";

type Sale = {
  id: string;
  organization_id?: string;
  sale_price_cents: number;
  currency_code: string;
  financial_status?: string;
  invoice_status?: string;
};
type Payment = {
  id: string;
  method_key: string;
  amount_cents: number;
  proof_name: string | null;
  proof_requirement_snapshot: string;
  state: string;
  created_at: string;
};
type Rule = {
  method_key: string;
  enabled: boolean;
  proof_requirement: "required" | "optional";
};
type Trade = { id: string; agreed_value_cents: number } | null;
type Closure = {
  acquisition_type: string;
  sale_price_cents: number;
  payment_total_cents: number;
  trade_in_cents: number;
  financing_cents: number;
  direct_payment_cents: number;
  owner_due_cents: number;
  owner_paid_cents: number;
  operating_cost_cents: number;
  commission_expected_cents: number;
  commission_paid_cents: number;
  expected_margin_cents: number;
  realized_margin_cents: number;
  status: "active" | "reversed";
};

const labels: Record<string, string> = {
  trade_in: "Retoma",
  cash: "Numerário",
  bank_transfer: "Transferência bancária",
  financing: "Financiamento",
  card: "Cartão",
  cheque: "Cheque",
  mbway: "MB WAY",
  other: "Outro",
};
const statusLabels: Record<string, string> = {
  in_process: "Em processo",
  payment_pending: "Pagamento por justificar",
  awaiting_validation: "A aguardar validação financeira",
  awaiting_invoice: "A aguardar fatura",
  financially_validated: "Financeiramente validada",
  finalized: "Finalizada",
  cancelled: "Cancelada",
};
const money = (c: number, code: string) =>
  new Intl.NumberFormat(code === "BRL" ? "pt-BR" : "pt-PT", {
    style: "currency",
    currency: code,
  }).format(c / 100);
const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error
    ? error.message
    : typeof error === "object" &&
        error !== null &&
        "message" in error &&
        typeof error.message === "string"
      ? error.message
      : fallback;

export function SaleFinancialPanel({
  sale,
  organizationId,
  tradeIn,
  writable,
  canValidate,
  onChanged,
}: {
  sale: Sale;
  organizationId: string;
  tradeIn: Trade;
  writable: boolean;
  canValidate: boolean;
  onChanged: () => Promise<void>;
}) {
  const [payments, setPayments] = useState<Payment[]>([]),
    [rules, setRules] = useState<Rule[]>([]),
    [closure, setClosure] = useState<Closure | null>(null),
    [notice, setNotice] = useState<string | null>(null),
    [saving, setSaving] = useState(false),
    [removing, setRemoving] = useState<Payment | null>(null),
    [removeReason, setRemoveReason] = useState("");
  const load = async () => {
    const [p, r, c] = await Promise.all([
      supabase
        .from("sale_payment_entries")
        .select(
          "id,method_key,amount_cents,proof_name,proof_requirement_snapshot,state,created_at",
        )
        .eq("sale_id", sale.id)
        .eq("state", "registered")
        .order("created_at"),
      supabase
        .from("organization_payment_method_settings")
        .select("method_key,enabled,proof_requirement")
        .eq("organization_id", organizationId)
        .eq("enabled", true),
      supabase
        .from("vehicle_sale_financial_closures")
        .select(
          "acquisition_type,sale_price_cents,payment_total_cents,trade_in_cents,financing_cents,direct_payment_cents,owner_due_cents,owner_paid_cents,operating_cost_cents,commission_expected_cents,commission_paid_cents,expected_margin_cents,realized_margin_cents,status",
        )
        .eq("sale_id", sale.id)
        .maybeSingle(),
    ]);
    setPayments((p.data ?? []) as Payment[]);
    setRules((r.data ?? []) as Rule[]);
    setClosure((c.data as Closure | null) ?? null);
    if (p.error || r.error || c.error)
      setNotice(
        p.error?.message ?? r.error?.message ?? c.error?.message ?? null,
      );
  };
  useEffect(() => void load(), [sale.id, organizationId]);
  const total = useMemo(
      () => payments.reduce((sum, item) => sum + item.amount_cents, 0),
      [payments],
    ),
    balance = sale.sale_price_cents - total,
    percentage = sale.sale_price_cents
      ? Math.min(100, Math.round((total / sale.sale_price_cents) * 100))
      : 100;
  const upload = async (file: File, kind: string) => {
    const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
    const path = `${organizationId}/${sale.id}/${kind}-${crypto.randomUUID()}-${safe}`;
    const { error } = await supabase.storage
      .from("sale-financial-documents")
      .upload(path, file);
    if (error) throw error;
    return { path, name: file.name };
  };
  const add = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget,
      data = new FormData(form),
      method = String(data.get("method")),
      file = data.get("proof") as File;
    setSaving(true);
    try {
      const proof = file?.size ? await upload(file, "payment") : null;
      const amount =
        method === "trade_in" && tradeIn
          ? tradeIn.agreed_value_cents
          : Math.round(Number(data.get("amount")) * 100);
      const { error } = await supabase.rpc("save_sale_payment", {
        p_sale_id: sale.id,
        p_method_key: method,
        p_amount_cents: amount,
        p_proof_path: proof?.path ?? null,
        p_proof_name: proof?.name ?? null,
        p_notes: data.get("notes") || null,
      });
      if (error) throw error;
      form.reset();
      await load();
      await onChanged();
      setNotice("Parcela adicionada.");
    } catch (error) {
      setNotice(errorMessage(error, "Não foi possível adicionar a parcela."));
    } finally {
      setSaving(false);
    }
  };
  const remove = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!removing) return;
    setSaving(true);
    const { error } = await supabase.rpc("void_sale_payment", {
      p_payment_id: removing.id,
      p_reason: removeReason,
    });
    setNotice(error?.message ?? "Parcela removida e preservada no histórico.");
    if (!error) {
      setRemoving(null);
      setRemoveReason("");
      await load();
      await onChanged();
    }
    setSaving(false);
  };
  const validate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const file = new FormData(event.currentTarget).get("invoice") as File;
    setSaving(true);
    try {
      const invoice = file?.size ? await upload(file, "invoice") : null;
      const { error } = await supabase.rpc("validate_sale_financial", {
        p_sale_id: sale.id,
        p_invoice_decision: invoice ? "attach" : "none",
        p_invoice_path: invoice?.path ?? null,
        p_invoice_name: invoice?.name ?? null,
        p_reason: null,
      });
      if (error) throw error;
      setNotice("Venda finalizada.");
      await load();
      await onChanged();
    } catch (error) {
      setNotice(errorMessage(error, "Não foi possível validar a venda."));
    } finally {
      setSaving(false);
    }
  };
  const attachLater = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const file = new FormData(event.currentTarget).get("invoice_later") as File;
    if (!file?.size) {
      setNotice("Selecione a fatura para continuar.");
      return;
    }
    setSaving(true);
    try {
      const invoice = await upload(file, "invoice");
      const { error } = await supabase.rpc("attach_sale_invoice", {
        p_sale_id: sale.id,
        p_invoice_path: invoice.path,
        p_invoice_name: invoice.name,
      });
      if (error) throw error;
      setNotice("Fatura associada à venda.");
      await onChanged();
    } catch (error) {
      setNotice(errorMessage(error, "Não foi possível carregar a fatura."));
    } finally {
      setSaving(false);
    }
  };
  const settleOwner = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget,
      data = new FormData(form),
      file = data.get("owner_proof") as File;
    if (!file?.size) {
      setNotice("Selecione o comprovativo do pagamento ao proprietário.");
      return;
    }
    setSaving(true);
    try {
      const proof = await upload(file, "owner-payment");
      const { error } = await supabase.rpc("settle_consignment_owner", {
        p_sale_id: sale.id,
        p_payment_method: String(data.get("owner_method")),
        p_paid_on: String(data.get("owner_paid_on")),
        p_storage_path: proof.path,
        p_original_name: proof.name,
        p_mime_type: file.type,
        p_size_bytes: file.size,
        p_notes: data.get("owner_notes") || null,
      });
      if (error) throw error;
      form.reset();
      await load();
      setNotice("Pagamento ao proprietário guardado e comprovativo associado.");
    } catch (error) {
      setNotice(
        errorMessage(
          error,
          "Não foi possível registar o pagamento ao proprietário.",
        ),
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="sale-financial" aria-busy={saving}>
      <div className="sale-financial-heading">
        <div>
          <h3>Composição do pagamento</h3>
          <p>
            Identifique 100% do valor recebido antes de finalizar a venda. A
            fatura é opcional.
          </p>
        </div>
        <strong>{statusLabels[sale.financial_status ?? "in_process"]}</strong>
      </div>
      {notice && (
        <button
          className="stock-notice"
          role="status"
          aria-live="polite"
          onClick={() => setNotice(null)}
        >
          {notice} ×
        </button>
      )}
      <div className="payment-progress">
        <div>
          <small>Valor da venda</small>
          <strong>{money(sale.sale_price_cents, sale.currency_code)}</strong>
        </div>
        <div>
          <small>Valor identificado</small>
          <strong>{money(total, sale.currency_code)}</strong>
        </div>
        <div>
          <small>Por identificar</small>
          <strong>{money(balance, sale.currency_code)}</strong>
        </div>
        <div>
          <small>Composição</small>
          <strong>{percentage}%</strong>
        </div>
        <span style={{ width: `${percentage}%` }} />
      </div>
      <div className="payment-list">
        {payments.map((payment) => (
          <article key={payment.id}>
            <div>
              <strong>{labels[payment.method_key]}</strong>
              <small>
                {payment.proof_name ??
                  (payment.proof_requirement_snapshot === "required"
                    ? "Comprovativo obrigatório"
                    : "Sem comprovativo")}
              </small>
            </div>
            <b>{money(payment.amount_cents, sale.currency_code)}</b>
            {writable &&
              !["finalized", "cancelled"].includes(
                sale.financial_status ?? "",
              ) && (
                <button type="button" onClick={() => setRemoving(payment)}>
                  Remover
                </button>
              )}
          </article>
        ))}
      </div>
      {closure && (
        <section className="sale-closure">
          <div className="sale-financial-heading">
            <div>
              <h3>Resultado financeiro da venda</h3>
              <p>
                {closure.acquisition_type === "consignment"
                  ? "Venda consignada: o valor do proprietário está separado da margem do stand."
                  : closure.trade_in_cents > 0
                    ? "Venda com retoma: a retoma integra o pagamento e entra no stock sem duplicar receita."
                    : "Venda de viatura própria com aquisição, custos e receita reconciliados."}
              </p>
            </div>
            <strong>
              {closure.status === "active" ? "Fecho ativo" : "Venda revertida"}
            </strong>
          </div>
          <div className="closure-grid">
            <div>
              <small>Venda</small>
              <strong>
                {money(closure.sale_price_cents, sale.currency_code)}
              </strong>
            </div>
            <div>
              <small>Retoma</small>
              <strong>
                {money(closure.trade_in_cents, sale.currency_code)}
              </strong>
            </div>
            <div>
              <small>Financiamento</small>
              <strong>
                {money(closure.financing_cents, sale.currency_code)}
              </strong>
            </div>
            <div>
              <small>Outros pagamentos</small>
              <strong>
                {money(closure.direct_payment_cents, sale.currency_code)}
              </strong>
            </div>
            <div>
              <small>Custos e proprietário</small>
              <strong>
                {money(closure.operating_cost_cents, sale.currency_code)}
              </strong>
            </div>
            <div>
              <small>Comissão prevista</small>
              <strong>
                {money(closure.commission_expected_cents, sale.currency_code)}
              </strong>
            </div>
            <div>
              <small>Margem prevista</small>
              <strong>
                {money(closure.expected_margin_cents, sale.currency_code)}
              </strong>
            </div>
            <div>
              <small>Margem realizada</small>
              <strong>
                {money(closure.realized_margin_cents, sale.currency_code)}
              </strong>
            </div>
          </div>
          {closure.owner_due_cents > 0 && (
            <div className="owner-settlement">
              <div>
                <small>Devido ao proprietário</small>
                <strong>
                  {money(closure.owner_due_cents, sale.currency_code)}
                </strong>
              </div>
              <div>
                <small>Pago ao proprietário</small>
                <strong>
                  {money(closure.owner_paid_cents, sale.currency_code)}
                </strong>
              </div>
              {canValidate &&
                closure.owner_paid_cents < closure.owner_due_cents &&
                closure.status === "active" && (
                  <form onSubmit={settleOwner}>
                    <label>
                      <span>Meio de pagamento *</span>
                      <input name="owner_method" required />
                    </label>
                    <label>
                      <span>Data *</span>
                      <input name="owner_paid_on" type="date" required />
                    </label>
                    <label>
                      <span>Comprovativo *</span>
                      <input
                        name="owner_proof"
                        type="file"
                        accept="application/pdf,image/jpeg,image/png,image/webp"
                        required
                      />
                    </label>
                    <label>
                      <span>Nota</span>
                      <input name="owner_notes" />
                    </label>
                    <button className="stock-primary" disabled={saving}>
                      Guardar
                    </button>
                  </form>
                )}
            </div>
          )}
        </section>
      )}
      {writable &&
        balance > 0 &&
        !["finalized", "cancelled", "awaiting_invoice"].includes(
          sale.financial_status ?? "",
        ) && (
          <form className="payment-add" onSubmit={add}>
            <label>
              <span>Meio de pagamento</span>
              <select name="method" required>
                {rules
                  .filter((rule) => rule.method_key !== "trade_in" || tradeIn)
                  .map((rule) => (
                    <option key={rule.method_key} value={rule.method_key}>
                      {labels[rule.method_key]}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              <span>Valor</span>
              <CurrencyInput
                currencyCode={sale.currency_code}
                name="amount"
                type="number"
                min="0.01"
                max={balance / 100}
                step="0.01"
                required
              />
            </label>
            <label>
              <span>Comprovativo</span>
              <input
                name="proof"
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
              />
              <small>
                A exigência é validada conforme a regra do meio escolhido.
              </small>
            </label>
            <label>
              <span>Nota</span>
              <input name="notes" />
            </label>
            <button className="stock-primary" disabled={saving}>
              {saving ? "A guardar…" : "Adicionar parcela"}
            </button>
          </form>
        )}
      {canValidate &&
        total === sale.sale_price_cents &&
        sale.financial_status === "awaiting_validation" && (
          <form className="financial-validation" onSubmit={validate}>
            <h3>Finalizar venda</h3>
            <p>
              O valor está 100% identificado. A fatura é opcional e pode ser
              associada agora ou posteriormente.
            </p>
            <label>
              <span>Fatura</span>
              <input
                name="invoice"
                type="file"
                accept="application/pdf,image/jpeg,image/png"
              />
            </label>
            <button className="stock-primary" disabled={saving}>
              {saving ? "A finalizar…" : "Finalizar venda"}
            </button>
          </form>
        )}
      {canValidate &&
        sale.financial_status === "finalized" &&
        sale.invoice_status !== "attached" && (
          <form className="financial-validation" onSubmit={attachLater}>
            <h3>Fatura da venda</h3>
            <p>
              A venda está finalizada. Se existir fatura, pode associá-la ao
              processo.
            </p>
            <label>
              <span>Fatura</span>
              <input
                name="invoice_later"
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                required
              />
            </label>
            <button className="stock-primary" disabled={saving}>
              {saving ? "A guardar…" : "Guardar"}
            </button>
          </form>
        )}
      {removing && (
        <div
          className="sale-payment-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="remove-payment-title"
        >
          <form onSubmit={remove}>
            <h3 id="remove-payment-title">Remover parcela</h3>
            <p>A parcela ficará preservada no histórico da venda.</p>
            <label>
              <span>Motivo</span>
              <textarea
                value={removeReason}
                onChange={(event) => setRemoveReason(event.target.value)}
                minLength={5}
                required
                autoFocus
              />
            </label>
            <footer>
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  setRemoving(null);
                  setRemoveReason("");
                }}
              >
                Cancelar
              </button>
              <button className="stock-primary" disabled={saving}>
                {saving ? "A remover…" : "Remover"}
              </button>
            </footer>
          </form>
        </div>
      )}
    </section>
  );
}
